










import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';

@Component({
  components: {
    Btn,
    Icon,
  },
})
export default class Pagination extends Vue {
  @Prop() private itemPluralName!: string;
  @Prop() private totalItems!: number;
  @Prop({ default: false }) private seeAll!: boolean;
  @Prop({ default: 1 }) private activePage!: number;
  @Prop({ default: 100 }) private itemsPerPage!: number;
  @Prop({ default: 5 }) private moreItemsPerPageMultiplier!: number;
  @Prop({ default: false }) private moreItemsPerPageShowed!: boolean;
  @Prop({ default: 100 }) private tooManyTotalPagesThreshold!: number;

  private iconEnum = IconEnum;
  private btnWidth = BtnWidth;
  private btnColor = BtnColor;

  get seeingAll() {
    return this.totalPages === 1 || this.totalPages === 0;
  }

  get startNumbers() {
    const arrayNumbers = [1];
    if (this.totalPages > 7 && this.activePage <= 5) {
      const numbers = [1, 2, 3, 4];
      for (const i of numbers) {
        if (i + 1 !== this.totalPages) {
          arrayNumbers.push(i + 1);
        }
      }
    } else if (this.totalPages <= 7) {
      const numbers = [1, 2, 3, 4, 5, 6];
      for (const i of numbers) {
        if (i + 1 < this.totalPages) {
          arrayNumbers.push(i + 1);
        }
      }
    }
    return arrayNumbers;
  }

  get middleNumbers() {
    const arrayNumbers = [];
    if (
      this.totalPages > 7 &&
      this.activePage > 5 &&
      this.activePage < this.totalPages - 4
    ) {
      const numbers = [-1, 0, 1];
      for (const i of numbers) {
        arrayNumbers.push(this.activePage + i);
      }
    }
    return arrayNumbers;
  }

  get endNumbers() {
    const arrayNumbers = [];
    if (this.totalPages > 1) {
      arrayNumbers.push(this.totalPages);
    }
    if (
      this.totalPages > 7 &&
      this.activePage >= this.totalPages - 4 &&
      this.activePage > 5
    ) {
      const numbers = [1, 2, 3, 4];
      for (const i of numbers) {
        arrayNumbers.unshift(this.totalPages - i);
      }
    }
    return arrayNumbers;
  }

  get offset() {
    return (this.activePage - 1) * this.itemsPerPage;
  }

  get lastItemShow() {
    let result = this.offset + this.itemsPerPage;
    if (result > this.totalItems) {
      result = this.totalItems;
    }
    return result;
  }

  get tooManyTotalPages() {
    return this.totalPages > this.tooManyTotalPagesThreshold;
  }

  get prevPageDisabled() {
    return this.activePage === 1;
  }

  get nextPageDisabled() {
    return this.activePage === this.totalPages;
  }

  get showFirstBetweenPages() {
    return this.totalPages > 7;
  }

  get showSecondBetweenPages() {
    return this.totalPages > 7 && this.middleNumbers.length > 0;
  }

  get totalPages() {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  // METHODS
  private emitSeeAll() {
    this.$emit('pagination-see-all', {
      offset: 0,
    });
  }

  private emitSeeMoreItemsPerPage() {
    this.$emit('pagination-more-items-per-page', {
      multiplier: this.moreItemsPerPageMultiplier,
    });
  }

  private emitSeeLessItemsPerPage() {
    this.$emit('pagination-less-items-per-page', {
      divider: this.moreItemsPerPageMultiplier,
    });
  }

  private goPage(pageNumber: number) {
    this.emitGoPage(pageNumber);
  }

  private goPrevPage() {
    this.emitGoPage(this.activePage - 1);
  }

  private goNextPage() {
    this.emitGoPage(this.activePage + 1);
  }

  private emitGoPage(pageNumber: number) {
    if (
      pageNumber !== this.activePage &&
      pageNumber > 0 &&
      pageNumber <= this.totalPages
    ) {
      this.$emit('pagination-go-page', {
        page: pageNumber,
        offset: (pageNumber - 1) * this.itemsPerPage,
        limit: this.itemsPerPage,
      });
    }
  }
}
